import { ArrowDown, ArrowUp, ClipboardCheck, History, Info, SlidersHorizontal, type LucideIcon } from 'lucide-react';
import { useProductStockMovements } from '@/hooks/use-product-intelligence';
import { getReportingTimeZone } from '@/lib/report-timezone';
import { formatQty, type MovementEntry } from '@/lib/product-intelligence';
import { cn } from '@/lib/utils';
import { IntelCard, IntelEmpty, IntelError, SectionLabel, SectionSkeleton } from './intelligence-ui';

interface Props {
  productId: string;
  /** The location whose ledger to show — movements are recorded per location. */
  locationId: string;
}

const KIND: Record<string, { icon: LucideIcon; verb: string; tone: string; tint: string }> = {
  stock_in: { icon: ArrowUp, verb: 'Stock In', tone: 'text-green-700', tint: 'bg-green-50' },
  stock_out: { icon: ArrowDown, verb: 'Stock Out', tone: 'text-red-600', tint: 'bg-red-50' },
  stock_count: { icon: ClipboardCheck, verb: 'Stock Count', tone: 'text-primary', tint: 'bg-primary/10' },
};
const ADJUSTMENT = { icon: SlidersHorizontal, verb: 'Adjustment', tone: 'text-muted-foreground', tint: 'bg-muted' };

/** "2026-10-27" in the reporting time zone — the business calendar day. */
function dayKey(d: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

function dayLabel(at: Date | null, now: Date, timeZone: string): string {
  if (!at) return 'Undated';
  const key = dayKey(at, timeZone);
  if (key === dayKey(now, timeZone)) return 'Today';
  if (key === dayKey(new Date(now.getTime() - 86_400_000), timeZone)) return 'Yesterday';
  return at.toLocaleDateString('en-US', { timeZone, day: 'numeric', month: 'short', year: 'numeric' });
}

function timeLabel(at: Date, timeZone: string): string {
  return at.toLocaleTimeString('en-US', { timeZone, hour: 'numeric', minute: '2-digit' });
}

/**
 * This product's stock-movement ledger at one location, newest first, grouped
 * by day. Every Stock In, Stock Out and Count recorded for the product — from
 * the POS, the mobile app or receiving — lands here.
 */
export function MovementPanel({ productId, locationId }: Props) {
  const { movements, total, isLoading, isError, error, refetch } = useProductStockMovements(productId, locationId);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <SectionSkeleton lines={3} />
        <SectionSkeleton lines={3} />
      </div>
    );
  }
  if (isError) {
    const message = (error as any)?.error?.message;
    return (
      <IntelError
        message={`Could not load movement history.${message ? ` ${message}` : ''}`}
        onRetry={() => refetch()}
      />
    );
  }
  if (movements.length === 0) {
    return (
      <IntelEmpty
        icon={History}
        title="No stock movements recorded"
        detail="Stock In, Stock Out and Count entries for this product at this location will appear here."
      />
    );
  }

  const timeZone = getReportingTimeZone();
  const now = new Date();
  const groups: { label: string; rows: MovementEntry[] }[] = [];
  for (const m of movements) {
    const label = dayLabel(m.createdAt, now, timeZone);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.rows.push(m);
    else groups.push({ label, rows: [m] });
  }

  return (
    <div className="space-y-5">
      {total !== undefined && total > movements.length && (
        <div className="flex items-start gap-2 rounded-lg bg-muted px-3 py-2.5 text-xs text-muted-foreground">
          <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Showing the {movements.length} most recent of {total} movements.
        </div>
      )}
      {groups.map((g) => (
        <section key={g.label}>
          <SectionLabel>{g.label}</SectionLabel>
          <IntelCard className="p-0">
            <ul className="divide-y divide-border">
              {g.rows.map((m, i) => (
                <MovementRow key={`${m.createdAt?.getTime() ?? 'x'}-${i}`} entry={m} timeZone={timeZone} />
              ))}
            </ul>
          </IntelCard>
        </section>
      ))}
    </div>
  );
}

function MovementRow({ entry: e, timeZone }: { entry: MovementEntry; timeZone: string }) {
  const k = KIND[e.type] ?? ADJUSTMENT;
  const Icon = k.icon;
  const delta = e.qtyAfter - e.qtyBefore;
  const meta = [e.userName, e.createdAt ? timeLabel(e.createdAt, timeZone) : null].filter(Boolean).join(' · ');
  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full', k.tint)}>
        <Icon className={cn('h-4 w-4', k.tone)} aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <span className="flex-1 text-sm font-medium text-foreground">{k.verb}</span>
          <span className={cn('text-sm font-semibold tabular-nums', delta === 0 ? 'text-muted-foreground' : k.tone)}>
            {delta > 0 ? `+${formatQty(delta)}` : formatQty(delta)}
          </span>
          <span className="text-xs tabular-nums text-muted-foreground">
            {formatQty(e.qtyBefore)} → {formatQty(e.qtyAfter)}
          </span>
        </div>
        {e.reason && <div className="mt-0.5 text-xs text-slate-700">{e.reason}</div>}
        {meta && <div className="mt-0.5 text-xs text-muted-foreground">{meta}</div>}
      </div>
    </li>
  );
}
